/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React from "react";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericCard from "@ui-components/Card/GenericCard";
import { Reflector } from "./ClusterModal";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import CustomRadioGroup from "@ui-components/RadioGroup/CustomRadioGroup";
import { ReactComponent as IconWarning } from "@shared/assets/Icons/Icon_Warning.svg";

interface ClusterStep3Props {
  clusterName: string;
  description: string;
  selectedReflectors: Reflector[];
  roles: Record<string, { role?: string; address?: string }>;
  setRoles: (val: Record<string, { role?: string; address?: string }>) => void;
  onBack: () => void;
  onSubmit: () => void;
}

const ClusterStep3: React.FC<ClusterStep3Props> = ({ clusterName, description, selectedReflectors, roles, setRoles, onBack, onSubmit }) => {
  const handleRoleChange = (id: string, role: string) => {
    // Convert role to lowercase
    const lowerRole = role.toLowerCase();
    
    // Create new roles object
    const newRoles = { ...roles };
    
    // If selecting primary, set the other reflector to secondary
    if (lowerRole === 'primary') {
      selectedReflectors.forEach(reflector => {
        if (reflector.id !== id) {
          newRoles[reflector.id] = { ...newRoles[reflector.id], role: 'secondary' };
        }
      });
    }
    
    // Set the selected role
    newRoles[id] = { ...newRoles[id], role: lowerRole };
    
    setRoles(newRoles);
  };

  const handleAddressChange = (id: string, address: string) => {
    setRoles({ ...roles, [id]: { ...roles[id], address } });
  };

  // Validate that exactly one primary is selected
  const hasPrimary = Object.values(roles).some(role => role.role === 'primary');
  const canSubmit = hasPrimary;

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-body-large font-bold mb-1">Cluster summary</h2>
      <GenericCard
        isCollapsible={false}
        bodyContent={
          <>
            <div className="text-body font-medium text-semantic-text-secondary mb-1">Cluster name</div>
            <div className="text-body text-semantic-text-primary mb-2">{clusterName}</div>
            <div className="text-body font-medium text-semantic-text-secondary mb-1">Description</div>
            <div className="text-body text-semantic-text-primary mb-2">{description || '-'}</div>
          </>
        }
      />
      <div className="mb-6 space-y-4">
        <div className="text-body-large font-semibold mb-3">Configure cluster members</div>
        {selectedReflectors.map((r: Reflector, idx: number) => (
          <GenericCard
            key={r.id}
            isCollapsible={false}
            bodyContent={
              <>
                <div className="flex flex-col items-start gap-4 mb-2">
                  <span className="text-base font-semibold text-semantic-text-primary">{r.name}</span>
                  <div className="flex flex-row ">
                  {r.data?.region && (
                    <span className="">
                      <GenericTag label={r.data.region} variant="neutral" />
                    </span>
                  )}
                  {r.licenseId && (
                    <span className="ml-2 px-2 py-0.5 rounded bg-gray-100 text-xs text-gray-700 flex items-center gap-1">
                      <svg className="w-3 h-3 inline-block text-gray-400" fill="currentColor" viewBox="0 0 20 20"><path d="M4 4h12v2H4V4zm0 4h12v2H4V8zm0 4h8v2H4v-2z"/></svg>
                      BYOL
                    </span>
                  )}
                  <span className="ml-2"><GenericTag label={r.statusPeer ? 'Active' : 'Inactive'} variant={r.statusPeer ? 'success' : 'default'} /></span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center mt-2">
                  <div>
                    <div className="text-body font-medium text-semantic-text-secondary mb-1">Role</div>
                    <CustomRadioGroup
                      name={`role-${r.id}`}
                      options={[
                        { value: 'primary', label: 'Primary' },
                        { value: 'secondary', label: 'Secondary' },
                      ]}
                      value={roles[r.id]?.role || ''}
                      onChange={(val) => handleRoleChange(r.id, val)}
                      className="gap-6"
                      direction="row"
                    />
                  </div>
                  <div>
                    <div className="text-body font-medium text-semantic-text-secondary mb-1">Local address for HA/Clustering</div>
                    <GenericInput
                      value={roles[r.id]?.address || ''}
                      onChange={val => handleAddressChange(r.id, val)}
                      className="w-full"
                    />
                  </div>
                </div>
              </>
            }
          />
        ))}
      </div>
      {!canSubmit && (
        <div className="bg-semantic-bg-error border-l-4 border-semantic-border-error p-4 text-semantic-text-error text-sm mb-6 flex items-start gap-2">
          <IconWarning className="w-8 h-8 mt-0.5 flex-shrink-0" />
          <span>Please select exactly one reflector as Primary.</span>
        </div>
      )}
      <div className="bg-semantic-bg-brand-primary border-l-4 border-semantic-border-brand-secondary p-4 text-semantic-text-brand text-sm mb-6 flex items-start gap-2">
        <IconWarning className="w-8 h-8 mt-0.5 flex-shrink-0" />
        <span>You are about to create a high-availability cluster with the configuration above. This will establish a HA/Clustering session between the selected CTX Reflectors.</span>
      </div>
      <div className="flex flex-col md:flex-row justify-between gap-2">
        <GenericButton label="Cancel" variant="secondary" onClick={onBack} />
        <div className="flex gap-2 justify-end w-full md:w-auto">
          <GenericButton label="Back" variant="secondary" onClick={onBack} />
          <GenericButton 
            label="Create cluster" 
            variant="brand" 
            onClick={onSubmit} 
            disabled={!canSubmit}
          />
        </div>
      </div>
    </div>
  );
};

export default ClusterStep3; 